import React, { useMemo, useState } from "react";
import { Check, Coffee, Maximize, Shield, Snowflake, Wifi } from "lucide-react";
import styles from "./RoomCard.module.scss";
import montereyImg from "../assets/img/jeremy-huang-2QZ3AnkVTEo-unsplash.jpg";
import { RoomCardProps } from "../types/room";
import { DEFAULT_ROOM_SEED, getRoomSeedByType, type RoomSeed } from "../data/roomseed";
import { Modal } from "./Modal";
import { HotelBookingWidget } from "./HotelBookingWidget";

const ICONS = {
    Maximize,
    Snowflake,
    Coffee,
    Wifi,
    Shield,
} as const;

/* Hotel offers carry their currency only as the symbol in the display
   price ("€83"), with `currency` left empty. */
const CURRENCY_BY_SYMBOL: Record<string, string> = { "€": "EUR", "$": "USD", "": "GBP" };

export const RoomCard: React.FC<RoomCardProps> = ({
    room,
    onReserve,
    staticCheckIn,
    staticCheckOut,
    hotelName,
}) => {
    const [isBookingOpen, setIsBookingOpen] = useState(false);
    const [activePhoto, setActivePhoto] = useState(0);
    const seed = getRoomSeedByType(room.type) || DEFAULT_ROOM_SEED;
    const cancellation = room.refundable ? "Free cancellation" : seed.cancellation;
    // `price` is the nightly rate as display text; the booking needs the
    // whole stay, which the offer carries as `totalPrice`.
    const stayTotal = useMemo(
        () => (typeof room.totalPrice === "number"
            ? room.totalPrice.toFixed(2)
            : String(room.price || "").replace(/[^0-9.]/g, "") || "0"),
        [room.totalPrice, room.price]
    );
    const currency = room.currency || CURRENCY_BY_SYMBOL[String(room.price || "").trim().charAt(0)] || "";
    // A reopened chat no longer has the search's dates in page state, but
    // a Hotelbeds rate key starts with them: "20261020|20261024|...".
    const [rateKeyIn, rateKeyOut] = String(room.rate_key || "")
        .split("|")
        .slice(0, 2)
        .map((part) => (/^\d{8}$/.test(part) ? `${part.slice(0, 4)}-${part.slice(4, 6)}-${part.slice(6)}` : ""));
    const normalizedCheckIn = staticCheckIn || (room as any).checkInDate || (room as any).check_in_date || rateKeyIn || "";
    const normalizedCheckOut = staticCheckOut || (room as any).checkOutDate || (room as any).check_out_date || rateKeyOut || "";
    const photos = room?.images?.length ? room.images : [room?.image || seed.photo || montereyImg];
    const photo = photos[activePhoto] || photos[0];

    const bookingData = {
        hotel: {
            name: hotelName || room.type || "Hotel",
            image: photo,
        },
        offers: [
            {
                checkInDate: normalizedCheckIn,
                checkOutDate: normalizedCheckOut,
                room: {
                    typeEstimated: {
                        category: room.type || "ROOM",
                    },
                },
                price: {
                    currency,
                    total: stayTotal,
                },
            },
        ],
    };

    const handleReserveClick = () => {
        onReserve?.(room);
        setIsBookingOpen(true);
    };

    return (
        <div className={`${styles.card} ${seed.bestMatch ? styles.cardBest : ""}`}>
            <div className={styles.photoWrap}>
                <img
                    src={photo}
                    alt={room?.type || "Room"}
                    className={styles.photo}
                />
                {photos.length > 1 && (
                    <div className={styles.photoDots}>
                        {photos.map((_, index) => (
                            <button
                                key={`${room.id}-photo-${index}`}
                                type="button"
                                className={`${styles.photoDot} ${index === activePhoto ? styles.photoDotActive : ''}`}
                                onClick={() => setActivePhoto(index)}
                                aria-label={`Show room photo ${index + 1}`}
                            />
                        ))}
                    </div>
                )}
                {seed.bestMatch && <span className={styles.bestBadge}>Best match</span>}
            </div>

            <div className={styles.body}>
                <h4 className={styles.name}>{room.type}</h4>
                <p className={styles.meta}>{room.board || seed.beds} · Sleeps {room.sleeps}</p>

                <div className={styles.amenities}>
                    {seed.amenities.map((item: RoomSeed["amenities"][number]) => {
                        const Icon = ICONS[item.icon];
                        return (
                            <div key={item.label} className={styles.amenity}>
                                <Icon size={12} />
                                <span>{item.label}</span>
                            </div>
                        );
                    })}
                </div>

                <div className={`${styles.cancel} ${cancellation === "Free cancellation" ? styles.cancelFree : styles.cancelNonref}`}>
                    {cancellation === "Free cancellation" && <Check size={12} />}
                    <span>{cancellation}</span>
                </div>

                <div className={styles.footer}>
                    <div className={styles.priceBlock}>
                        <span className={styles.price}>{room.price}</span>
                    </div>

                    <button className={styles.reserveBtn} onClick={handleReserveClick}>
                        Reserve
                    </button>
                </div>
            </div>

            <Modal isOpen={isBookingOpen} onClose={() => setIsBookingOpen(false)}>
                <HotelBookingWidget
                    booking={bookingData}
                    onCancel={() => setIsBookingOpen(false)}
                    onComplete={() => setIsBookingOpen(false)}
                    onViewBookings={() => setIsBookingOpen(false)}
                />
            </Modal>
        </div>
    );
};
